/**
 * GoogleOAuthCallback Component
 * Handles the OAuth callback from Google
 * Processes the authorization code and closes the popup
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Spinner, Alert, Container } from 'react-bootstrap';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { api } from '../../../api/Middleware';

const GoogleOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const { agentId } = useParams();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Handle user denial
      if (errorParam) {
        setStatus('error');
        setError(errorParam === 'access_denied'
          ? 'Autorização negada pelo usuário.'
          : `Erro: ${errorParam}`
        );
        notifyParent('error', errorParam);
        return;
      }

      // Check for authorization code
      if (!code) {
        setStatus('error');
        setError('Código de autorização não recebido.');
        notifyParent('error', 'no_code');
        return;
      }

      try {
        // Exchange code for tokens via backend
        const response = await api.get('/agents_flow/google/callback/', {
          params: {
            code,
            state,
            agent_flow_id: agentId
          }
        });

        if (response.data?.success) {
          setStatus('success');
          notifyParent('success');

          // Close popup after showing success
          setTimeout(() => {
            window.close();
          }, 1500);
        } else {
          setStatus('error');
          setError(response.data?.error || 'Erro ao processar autorização.');
          notifyParent('error', response.data?.error);
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        const errorMessage = err.response?.data?.error || err.message || 'Erro ao conectar com o servidor.';
        setError(errorMessage);
        notifyParent('error', errorMessage);
      }
    };

    const notifyParent = (type, errorMsg = null) => {
      if (window.opener) {
        window.opener.postMessage(
          {
            type: type === 'success' ? 'google-oauth-success' : 'google-oauth-error',
            error: errorMsg
          },
          '*'
        );
      }
    };

    processCallback();
  }, [searchParams, agentId]);

  return (
    <Container className="d-flex flex-column align-items-center justify-content-center min-vh-100">
      {status === 'processing' && (
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
          <h4>Processando autorização...</h4>
          <p className="text-muted">Aguarde enquanto conectamos sua conta Google.</p>
        </div>
      )}

      {status === 'success' && (
        <Alert variant="success" className="text-center" style={{ maxWidth: '400px' }}>
          <IconCheck size={48} className="text-success mb-3" />
          <Alert.Heading>Conectado com sucesso!</Alert.Heading>
          <p className="mb-0">
            Sua conta Google foi conectada. Esta janela fechará automaticamente.
          </p>
        </Alert>
      )}

      {status === 'error' && (
        <Alert variant="danger" className="text-center" style={{ maxWidth: '400px' }}>
          <IconAlertCircle size={48} className="text-danger mb-3" />
          <Alert.Heading>Erro na autorização</Alert.Heading>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.close(); }}
              className="alert-link"
            >
              Fechar janela
            </a>
          </p>
        </Alert>
      )}
    </Container>
  );
};

export default GoogleOAuthCallback;
